import { ReactNode } from "react";
import { motion } from "framer-motion";
import { useReducedMotion } from "@/hooks/useReducedMotion";

interface StaggerChildrenProps {
  children: ReactNode;
  /**
   * Delay between each child animation in seconds
   * @default 0.05
   */
  staggerDelay?: number;
  /**
   * Animation variant
   * @default 'fade'
   */
  variant?: 'fade' | 'slide' | 'scale';
  /**
   * Initial animation state
   * @default true
   */
  animate?: boolean;
}

/**
 * StaggerChildren animates children in sequence for a polished entrance effect
 * 
 * Usage:
 * ```tsx
 * <StaggerChildren>
 *   <div>Item 1</div>
 *   <div>Item 2</div>
 *   <div>Item 3</div>
 * </StaggerChildren>
 * ```
 */
export function StaggerChildren({
  children,
  staggerDelay = 0.05,
  variant = 'fade',
  animate = true,
}: StaggerChildrenProps) {
  const prefersReducedMotion = useReducedMotion();

  if (prefersReducedMotion || !animate) {
    return <>{children}</>;
  }

  const variants = {
    fade: {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
    },
    slide: {
      initial: { opacity: 0, y: 10 },
      animate: { opacity: 1, y: 0 },
    },
    scale: {
      initial: { opacity: 0, scale: 0.95 },
      animate: { opacity: 1, scale: 1 },
    },
  };

  const selectedVariant = variants[variant];

  return (
    <>
      {React.Children.map(children, (child, index) => {
        if (!React.isValidElement(child)) {
          return child;
        }

        return (
          <motion.div
            key={index}
            initial={selectedVariant.initial}
            animate={selectedVariant.animate}
            transition={{
              duration: 0.2,
              delay: index * staggerDelay,
              ease: [0.4, 0, 0.2, 1],
            }}
          >
            {child}
          </motion.div>
        );
      })}
    </>
  );
}

